import React, {
  useEffect, useState, useRef, Fragment,
} from 'react';
import { ConnectedProps, connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import cloneDeep from 'lodash.clonedeep';
import { FieldUpdateHandlerParam } from 'shared/form';
import { FormLayoutContent, FormLayoutFooter, FormLayout } from 'components/FormLayout';
import FormButtons from 'components/FormButtons';
import FormPanel from 'components/FormPanel';
import PeriodicAuditForm from 'routes/PerodicAudit/components/PeriodicAuditForm';
import { Ref } from 'routes/PerodicAudit/components/PeriodicAuditForm/PeriodicAuditForm';
import { RootState } from 'data/root.reducers';
import * as selectors from 'data/periodicAudit/periodicAudit.selectors';
import * as actions from 'data/periodicAudit/periodicAudit.actions';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import { PeriodicAudit } from 'data/periodicAudit/periodicAudit.types';
import { periodicAuditTemplate } from '../../templates';
import { adaptTemplateVisibilityMode, adaptTemplateOptions } from '../../utils/PeriodicAudit.adapter';

type RouterParams = {
  history: string
}
const mapState = (state: RootState) => ({
  periodicAuditTemplateDef: adaptTemplateVisibilityMode(periodicAuditTemplate, 'view'),
  deliveryFunctionId: state.global.selectedDF,
  userId: state.authentication.userId,
  userName: authenticationSelectors.getAuthenticationUserId(state),
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  selectedPeriodicAFAudit: selectors.getSelectedPeriodicAudit(state)[0],
  periodicAudit: selectors.getPeriodicAudit(state),
  originalSelectedPeriodicAFAudit: selectors.getSelectedPeriodicAudit(state)[0],
});

const mapDispatch = {
  updatePeriodicAudit: actions.updatePeriodicAudit,
  getPeriodicAudit: actions.getPeriodicAudit,
  goToPeriodicAuditEdit: actions.goToPeriodicAuditEdit,
};
const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>
type PropsFromRouter = RouteComponentProps<RouterParams>

export type Props = PropsFromRedux & PropsFromRouter;

export const PeriodicAuditView = ({
  history,
  selectedPeriodicAFAudit,
  periodicAudit,
  deliveryFunctionId,
  periodicAuditTemplateDef,
  goToPeriodicAuditEdit,
  getPeriodicAudit,
}: Props): JSX.Element => {

  const formRef = useRef<Ref>(null);
  const [formData, setFormData] = useState<Partial<PeriodicAudit>>({});
  const [template, setTemplate] = useState(periodicAuditTemplateDef);
  const { t } = useTranslation();

  useEffect(() => {
    getPeriodicAudit(deliveryFunctionId);
  }, [deliveryFunctionId, getPeriodicAudit]);

  useEffect(() => {
    if (periodicAudit != null && template != null) {
      const clonedTemplate = cloneDeep(template);
      setFormData(periodicAudit);
      clonedTemplate.formId = Date.now().toString();
      clonedTemplate.key = Date.now().toString();

      if (clonedTemplate.main) {
        const days = clonedTemplate.main.fields;
        const period = periodicAudit.CAUDPERIOD;

        days.monday.initial = Number(period.charAt(0));
        days.tuesday.initial = Number(period.charAt(1));
        days.wednesday.initial = Number(period.charAt(2));
        days.thursday.initial = Number(period.charAt(3));
        days.friday.initial = Number(period.charAt(4));
        days.saturday.initial = Number(period.charAt(5));
        days.sunday.initial = Number(period.charAt(6));
      }

      setTemplate(clonedTemplate);
    }
  }, [
    periodicAudit,
  ]);

  const handleClickEdit = () => goToPeriodicAuditEdit(history);

  const handleFieldUpdate = ({ name: fieldName, value }: FieldUpdateHandlerParam) => {
    if (formData != null) {
      return;
    }

    const clonedFormData = cloneDeep(formData as PeriodicAudit);

    setFormData({
      ...clonedFormData,
      [fieldName]: value,
    });
  };

  const renderForm = () => {
    if (formData && template) {
      return (
        <PeriodicAuditForm
          ref={formRef}
          data={formData}
          template={template}
          isEditing
          onFieldUpdate={handleFieldUpdate}
        />
      );
    }
  };
  const renderFormPanel = () => {
    return (
      <FormPanel>
        <FormButtons
          isEditing={false}
          onClickEdit={handleClickEdit}
        />
      </FormPanel>
    );
  };

  return (
    <div data-testid="PeriodicAuditView">
      <FormLayout>
        <FormLayoutContent>
          {renderFormPanel()}
          <div className="read-only">
            {renderForm()}
          </div>
        </FormLayoutContent>
      </FormLayout>
    </div>
  );
};

export default withRouter(connector(PeriodicAuditView));
